// KanbanColumn — single status column (pending / in_progress / completed /
// abandoned) in the Wishes.tsx kanban.
//
// Per `docs/spec/wishlist/DESIGN.md §5.2`:
//   - paper-deep bg (not v2 `bg-surface` white)
//   - hairline bottom border on header
//   - column header: status label + count, color per status
//   - soft WIP badge: paper-deep badge "建議先完成手上的" when
//     in_progress count > 5 (per Q-new-2 a)
//
// Per `docs/spec/wishlist/PORT_PLAN.md §3.2` token replacement:
//   - `bg-paper-deep` (not `bg-surface` / not v2 `bg-pending-8%`)
//   - hairline border (not 4-color role strip)
//   - 2 elevation tiers only (per DESIGN §6.1)
//
// Drop handling: this column body is a dnd-kit `useDroppable` zone; the
// route wires the global `onDragEnd` handler in `Wishes.tsx` via
// `<DndContext>`. The column id passed to dnd-kit is `column-${status}`
// so the route can resolve `over.id → WishStatus` via a small lookup.

import { useDroppable } from "@dnd-kit/core";
import { forwardRef } from "react";
import { Star, Hourglass, Check, X } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import type { WishStatus } from "@/lib/api/wishlist";
import { WISH_STATUS_LABEL } from "@/lib/i18n/wishlist";

export interface KanbanColumnProps {
  /** Wish status this column represents. */
  status: WishStatus;
  /** Display label (繁體中文) — typically a state noun. */
  label: string;
  /** Number of wishes currently in this column. */
  count: number;
  /** Optional soft WIP badge ("建議先完成手上的" when in_progress > 5). */
  wipBadge?: string;
  /** Children render inside the scrollable drop area. */
  children: React.ReactNode;
  className?: string;
}

// W53.1 D-6: the labels moved to `lib/i18n/wishlist.ts`; this map keeps only
// what is genuinely per-column decoration (icon + dot colour).
const STATUS_META: Record<
  WishStatus,
  { icon: typeof Star; labelZh: string; dotClass: string }
> = {
  pending: { icon: Star, labelZh: WISH_STATUS_LABEL.pending, dotClass: "bg-ink-3" },
  in_progress: {
    icon: Hourglass,
    labelZh: WISH_STATUS_LABEL.in_progress,
    dotClass: "bg-you-strong",
  },
  completed: {
    icon: Check,
    labelZh: WISH_STATUS_LABEL.completed,
    dotClass: "bg-shared-strong",
  },
  abandoned: { icon: X, labelZh: WISH_STATUS_LABEL.abandoned, dotClass: "bg-ink-3" },
};

export const KanbanColumn = forwardRef<HTMLDivElement, KanbanColumnProps>(
  function KanbanColumn(
    { status, label, count, wipBadge, children, className },
    ref,
  ) {
    const meta = STATUS_META[status];
    const Icon = meta.icon;
    const isAbandoned = status === "abandoned";
    const { setNodeRef, isOver } = useDroppable({
      id: `column-${status}`,
    });
    return (
      <div
        ref={(el) => {
          // Compose the forwarded ref + the dnd-kit setNodeRef so the
          // route can still attach its own ref if needed. Coerce the
          // wider `HTMLElement | null` callback type to our narrower
          // `HTMLDivElement | null` at the boundary.
          const div = el as HTMLDivElement | null;
          setNodeRef(div);
          if (typeof ref === "function") ref(div);
          else if (ref) {
            (ref as React.MutableRefObject<HTMLDivElement | null>).current = div;
          }
        }}
        data-component="KanbanColumn"
        data-status={status}
        data-testid={`kanban-column-${status}`}
        className={cn(
          // base: paper-deep bg + hairline border + flex column
          "bg-paper-deep border border-hairline rounded-2xl flex flex-col min-w-[260px] flex-1",
          // abandoned column gets a subtle opacity-70 per DESIGN §3.6
          isAbandoned && "opacity-70",
          // highlight when a card hovers over this column
          isOver && "ring-2 ring-you-strong/40",
          className,
        )}
      >
        <header
          data-component="KanbanColumnHeader"
          className={cn(
            "flex items-center gap-2 px-s-4 py-s-3 border-b border-hairline",
          )}
        >
          <span
            data-testid="kanban-column-dot"
            className={cn("h-2 w-2 rounded-full shrink-0", meta.dotClass)}
            aria-hidden
          />
          <Icon
            className={cn(
              "h-4 w-4 shrink-0",
              status === "in_progress" ? "text-you-strong" : "text-ink-2",
            )}
            aria-hidden
          />
          <h2
            data-testid="kanban-column-label"
            className={cn(
              "text-body-sm font-semibold truncate flex-1",
              status === "in_progress" ? "text-you-strong" : "text-ink-1",
            )}
          >
            {label}
          </h2>
          <span
            data-testid="kanban-column-count"
            className="text-meta font-semibold text-ink-3 tabular-nums"
            aria-label={`${meta.labelZh} 共 ${count} 個`}
          >
            {count}
          </span>
        </header>
        {wipBadge ? (
          <div
            data-component="KanbanWipBadge"
            data-testid="kanban-wip-badge"
            className="px-s-3 py-1.5 mx-s-3 mt-s-2 bg-paper-deep border border-hairline rounded-md text-meta text-ink-2"
          >
            {wipBadge}
          </div>
        ) : null}
        <div
          data-component="KanbanColumnBody"
          data-testid="kanban-column-body"
          className="flex-1 min-h-[120px] px-s-2 py-s-2 flex flex-col gap-s-2 overflow-y-auto"
        >
          {children}
        </div>
      </div>
    );
  },
);
